import Phaser from "phaser";
import {
  ARENA_COLS,
  ARENA_ROWS,
  type Direction,
  Directions,
  MANA_REGEN_AMOUNT,
  MANA_REGEN_INTERVAL_MS,
  MOVE_DURATION_MS,
  TILE_SIZE,
  VOCATION_STATS,
} from "../config";
import type { PlayerConfig, Vocation } from "../index";
import { SPRITE_FRAMES, SPRITESHEET_KEY, SPRITESHEET_TILE } from "../sprites";

const PLAYER_SCALE = TILE_SIZE / SPRITESHEET_TILE; // scales 16px sprite to fill 32px tile

export type WhichPlayer = "player1" | "player2";

interface PlayerEvents {
  damaged: (amount: number, newHp: number) => void;
  healed: (amount: number, newHp: number) => void;
  manaChanged: (newMana: number) => void;
  died: () => void;
}

/**
 * One of the two combatants. Owns its own stats and sprite; the scene tells
 * it when to move/attack/heal and listens for events when something changes.
 *
 * The split between "Player state" and "scene rules" is deliberate — Player
 * doesn't know about the other player, fire tiles, or projectiles. The scene
 * coordinates collisions and asks the Player to take damage when relevant.
 */
export class Player {
  readonly which: WhichPlayer;
  readonly name: string;
  readonly vocation: Vocation;
  readonly maxHp: number;
  readonly maxMana: number;
  readonly fistMax: number;
  readonly magicLevel: number;
  readonly healDie: number;

  hp: number;
  mana: number;
  col: number;
  row: number;
  direction: Direction;
  isAlive = true;

  readonly sprite: Phaser.GameObjects.Sprite;

  private readonly scene: Phaser.Scene;
  private readonly events = new Phaser.Events.EventEmitter();
  private readonly canMoveTo: (col: number, row: number) => boolean;
  private isMoving = false;
  private manaRegenTimer: Phaser.Time.TimerEvent;

  constructor(
    scene: Phaser.Scene,
    which: WhichPlayer,
    config: PlayerConfig,
    spawn: { col: number; row: number },
    initialDirection: Direction,
    canMoveTo: (col: number, row: number) => boolean,
  ) {
    this.scene = scene;
    this.which = which;
    this.name = config.name;
    this.vocation = config.vocation;

    const stats = VOCATION_STATS[config.vocation];
    this.maxHp = stats.maxHp;
    this.maxMana = stats.maxMana;
    this.fistMax = stats.fistMax;
    this.magicLevel = stats.magicLevel;
    this.healDie = stats.healDie;

    this.hp = this.maxHp;
    this.mana = this.maxMana;
    this.col = spawn.col;
    this.row = spawn.row;
    this.direction = initialDirection;

    this.canMoveTo = canMoveTo;

    const frame =
      which === "player1" ? SPRITE_FRAMES.player1 : SPRITE_FRAMES.player2;
    this.sprite = scene.add.sprite(this.pixelX, this.pixelY, SPRITESHEET_KEY, frame);
    this.sprite.setScale(PLAYER_SCALE);
    this.sprite.setDepth(10);
    // Tint to keep the two players visually distinct even if their underlying
    // sprite frames are similar Kenney creatures.
    this.sprite.setTint(which === "player1" ? 0xf97316 : 0x22d3ee);

    // Add an immovable arcade body so projectile overlap fires. The body
    // tracks the sprite (which is repositioned by tweens during movement);
    // `setImmovable(true)` prevents projectile impacts from pushing it.
    // Body size is in pre-scale TEXTURE pixels — 12px → 24px after PLAYER_SCALE.
    scene.physics.add.existing(this.sprite);
    const body = this.sprite.body as Phaser.Physics.Arcade.Body;
    body.setSize(12, 12);
    body.setOffset(2, 2);
    body.setImmovable(true);

    this.manaRegenTimer = scene.time.addEvent({
      delay: MANA_REGEN_INTERVAL_MS,
      callback: () => this.regenerateMana(),
      loop: true,
    });
  }

  get pixelX(): number {
    return this.col * TILE_SIZE + TILE_SIZE / 2;
  }
  get pixelY(): number {
    return this.row * TILE_SIZE + TILE_SIZE / 2;
  }

  on<E extends keyof PlayerEvents>(event: E, fn: PlayerEvents[E]): this {
    this.events.on(event, fn);
    return this;
  }

  destroy(): void {
    this.manaRegenTimer.destroy();
    this.events.removeAllListeners();
    this.sprite.destroy();
  }

  /**
   * Tries to move one tile in the given direction. Updates facing direction
   * even if the move is blocked, so the player can "turn in place" by pressing
   * a direction into a wall — needed to aim fire blasts.
   */
  attemptMove(dx: number, dy: number): boolean {
    if (!this.isAlive || this.isMoving) return false;

    // Update facing first so attacks aim correctly even when blocked.
    this.direction =
      dx < 0
        ? Directions.Left
        : dx > 0
          ? Directions.Right
          : dy < 0
            ? Directions.Up
            : Directions.Down;

    const newCol = this.col + dx;
    const newRow = this.row + dy;
    if (newCol < 0 || newCol >= ARENA_COLS) return false;
    if (newRow < 0 || newRow >= ARENA_ROWS) return false;
    if (!this.canMoveTo(newCol, newRow)) return false;

    this.col = newCol;
    this.row = newRow;
    this.isMoving = true;
    this.scene.tweens.add({
      targets: this.sprite,
      x: this.pixelX,
      y: this.pixelY,
      duration: MOVE_DURATION_MS,
      onComplete: () => {
        this.isMoving = false;
      },
    });
    return true;
  }

  takeDamage(amount: number): void {
    if (!this.isAlive || amount <= 0) return;
    this.hp = Math.max(0, this.hp - amount);
    this.events.emit("damaged", amount, this.hp);

    this.scene.tweens.add({
      targets: this.sprite,
      alpha: 0.3,
      duration: 60,
      yoyo: true,
    });

    if (this.hp === 0) {
      this.isAlive = false;
      this.sprite.setAlpha(0.25);
      this.events.emit("died");
    }
  }

  /** Heal capped at maxHp — fixes the 2011 overheal bug. */
  heal(amount: number): void {
    if (!this.isAlive) return;
    const before = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    const delta = this.hp - before;
    if (delta > 0) this.events.emit("healed", delta, this.hp);
  }

  spendMana(cost: number): boolean {
    if (!this.isAlive || this.mana < cost) return false;
    this.mana -= cost;
    this.events.emit("manaChanged", this.mana);
    return true;
  }

  private regenerateMana(): void {
    if (!this.isAlive || this.mana >= this.maxMana) return;
    this.mana = Math.min(this.maxMana, this.mana + MANA_REGEN_AMOUNT);
    this.events.emit("manaChanged", this.mana);
  }

  /** 8-directional adjacency (matches 2011 verificaProximidade). */
  isAdjacentTo(other: Player): boolean {
    if (this === other) return false;
    return (
      Math.abs(this.col - other.col) <= 1 &&
      Math.abs(this.row - other.row) <= 1
    );
  }
}
